import { useNavigate } from '@tanstack/react-router';
import { useClerk } from '@clerk/clerk-react';
import { useEffect, useRef } from 'react';
import { ArrowRight, LogOut } from 'lucide-react';
import logo from '@/assets/logo.png';

interface SubscriptionModalProps {
  showModal: boolean;
  userId?: string;
}

export default function SubscriptionModal({ showModal, userId: _userId }: SubscriptionModalProps) {
  const navigate = useNavigate();
  const { signOut } = useClerk();
  const primaryRef = useRef<HTMLButtonElement>(null);

  // Focus the primary CTA when the modal opens
  useEffect(() => {
    if (showModal) {
      // Small delay to ensure the element is rendered
      const timer = setTimeout(() => primaryRef.current?.focus(), 100);
      return () => clearTimeout(timer);
    }
  }, [showModal]);

  // Trap focus inside the modal and block scrolling
  useEffect(() => {
    if (!showModal) return;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, [showModal]);

  if (!showModal) return null;

  const handleSubscribe = () => {
    navigate({ to: '/subscribe', search: {} as any });
  };

  const handleSignOut = () => {
    signOut();
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-white/80 backdrop-blur-xl p-4 sm:p-6"
      role="dialog"
      aria-modal="true"
      aria-labelledby="sub-modal-title"
    >
      <div className="w-full max-w-md mx-auto">
        <div className="rounded-2xl bg-white shadow-xl ring-1 ring-slate-200 overflow-hidden">
          {/* Header accent */}
          <div className="h-1.5 bg-gradient-to-r from-violet-500 to-violet-600" />

          <div className="p-6 sm:p-8 space-y-6">
            {/* Logo */}
            <div className="flex justify-center">
              <img src={logo} alt="GranGuard" className="h-8 sm:h-9" />
            </div>

            <div className="text-center space-y-3">
              <h2 id="sub-modal-title" className="text-xl sm:text-2xl font-semibold text-slate-900">
                Set up your protection plan
              </h2>
              <p className="text-base text-slate-500 leading-relaxed max-w-sm mx-auto">
                Choose a plan to unlock scam defence training, threat alerts, and family protection tools.
              </p>
            </div>

            {/* Features preview */}
            <ul className="space-y-2.5 text-sm text-slate-700">
              {[
                'Interactive scam defence courses',
                'AI-powered link and message scanning',
                'Personal data breach alerts',
                'Family protection dashboard',
              ].map((feature) => (
                <li key={feature} className="flex items-start gap-2.5">
                  <svg className="w-4 h-4 mt-0.5 text-violet-500 shrink-0" viewBox="0 0 16 16" fill="currentColor">
                    <path fillRule="evenodd" d="M8 15A7 7 0 108 1a7 7 0 000 14zm3.28-8.72a.75.75 0 00-1.06-1.06L7 8.44 5.78 7.22a.75.75 0 00-1.06 1.06l1.75 1.75a.75.75 0 001.06 0l3.75-3.75z" clipRule="evenodd" />
                  </svg>
                  <span>{feature}</span>
                </li>
              ))}
            </ul>

            {/* Actions */}
            <div className="space-y-3 pt-1">
              <button
                ref={primaryRef}
                type="button"
                onClick={handleSubscribe}
                className="flex h-12 w-full cursor-pointer items-center justify-center gap-2 rounded-xl bg-violet-600 text-white font-semibold shadow-sm hover:bg-violet-700 active:bg-violet-800 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-500 focus-visible:ring-offset-2"
              >
                Choose your plan
                <ArrowRight className="w-4 h-4" />
              </button>

              <button
                type="button"
                onClick={handleSignOut}
                className="flex w-full cursor-pointer items-center justify-center gap-2 rounded-xl px-4 py-2.5 text-sm font-medium text-slate-500 hover:text-slate-700 hover:bg-slate-50 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-500 focus-visible:ring-offset-2"
              >
                <LogOut className="w-3.5 h-3.5" />
                Sign out
              </button>
            </div>

            <p className="text-center text-xs text-slate-400">
              Plans start at $4.99/month. Cancel anytime.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
